/**
 * PGD 4b: Clarithromycin for infected insect bites and stings
 * Version: 1.0
 * Effective: 2024-01-31
 * Expiry: 2027-01-30
 *
 * QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
 */

import type { PGD } from "../types";

export const pgdClarithromycinInsectBites: PGD = {
  id: "pgd-4b-clarithromycin-insectbites",
  reference: "4b",
  version: "1.0",
  drugRef: "clarithromycin",
  indication:
    "Infected insect bite(s) and sting(s) — penicillin allergic, not pregnant",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",

  population: {
    ageRange: { min: 1, max: 999 },
    pregnancyAllowed: false,
    breastfeedingAllowed: true,
    notes: "Children 1–11 require weight for dosing.",
  },

  duration: { days: 5 },
  requiresWeightInput: { min: 1, max: 11 },

  doseBands: [
    {
      id: "clari-under8kg",
      resolveBy: "weight",
      weightRange: { minKg: 0, maxKg: 7.99 },
      dose: 0,
      mgPerKg: 7.5,
      frequency: "TWICE a day",
      comment:
        "Formula band: dose mg = weight × 7.5 mg/kg. Engine substitutes {dose} and {volume}.",
      formulations: [
        {
          formulationId: "clari-250-5ml",
          instruction: "Give {volume} ({dose}mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give {volume} ({dose}mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
      ],
    },
    {
      id: "clari-8-11kg",
      resolveBy: "weight",
      weightRange: { minKg: 8, maxKg: 11.99 },
      dose: 62.5,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 1.25mL (62.5mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 2.5mL (62.5mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
      ],
    },
    {
      id: "clari-12-19kg",
      resolveBy: "weight",
      weightRange: { minKg: 12, maxKg: 19.99 },
      dose: 125,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 2.5mL (125mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 5mL (125mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
      ],
    },
    {
      id: "clari-20-29kg",
      resolveBy: "weight",
      weightRange: { minKg: 20, maxKg: 29.99 },
      dose: 187.5,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 3.75mL (187.5mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 7.5mL (187.5mg) TWICE a day for 5 days",
          quantity: { bottles: 2, label: "2 × 70mL bottles" },
        },
      ],
    },
    {
      id: "clari-30-40kg",
      resolveBy: "weight",
      weightRange: { minKg: 30, maxKg: 40 },
      dose: 250,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-250-tab",
          instruction: "Take ONE 250mg tablet TWICE a day for 5 days",
          quantity: { count: 10, label: "10 tablets" },
        },
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 5mL (250mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 10mL (250mg) TWICE a day for 5 days",
          quantity: { bottles: 2, label: "2 × 70mL bottles" },
        },
      ],
    },
    {
      id: "clari-12plus",
      resolveBy: "age",
      ageRange: { min: 12, max: 999 },
      dose: 500,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-500-tab",
          instruction: "Take ONE 500mg tablet TWICE a day for 5 days",
          quantity: { count: 10, label: "10 tablets" },
        },
        {
          formulationId: "clari-250-tab",
          instruction: "Take TWO 250mg tablets TWICE a day for 5 days",
          quantity: { count: 20, label: "20 tablets" },
        },
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 10mL (500mg) TWICE a day for 5 days",
          quantity: { bottles: 2, label: "2 × 70mL bottles" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 20mL (500mg) TWICE a day for 5 days",
          quantity: { bottles: 3, label: "3 × 70mL bottles" },
        },
      ],
    },
  ],

  preSupplyChecks: [
    { id: "ex-consent-refused", question: "Has the patient/carer refused consent?" },
    {
      id: "ex-unable-swallow",
      question:
        "Unable to absorb oral medications and/or unable to swallow oral dosage formulations?",
    },
    {
      id: "ex-macrolide-allergy",
      question: "Allergy to clarithromycin or any macrolide?",
    },
    {
      id: "ex-long-term-macrolide",
      question:
        "Currently on long-term clarithromycin or another macrolide antibiotic?",
      hint:
        "e.g. erythromycin for asplenia prophylaxis, azithromycin for COPD/bronchiectasis",
    },
    { id: "ex-keto", question: "Ketogenic diet?" },
    {
      id: "ex-failed-abx",
      question:
        "Failed previous antibiotic for this episode of infected insect bite or sting?",
    },
    {
      id: "ex-mrsa",
      question: "Previous or current known MRSA colonisation or infection?",
    },
    { id: "ex-myasthenia", question: "Known myasthenia gravis?" },
    {
      id: "ex-qt",
      question:
        "History of QT prolongation (congenital or acquired) or ventricular cardiac arrhythmia including torsades de pointes?",
      linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
    },
    {
      id: "ex-qt-meds",
      question: "Taking any medicine known to cause QT prolongation?",
      linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
    },
    {
      id: "ex-electrolyte",
      question:
        "Known electrolyte disturbances (hypokalaemia or hypomagnesaemia)?",
    },
    {
      id: "ex-ckd5",
      question: "Known CKD stage 5 (eGFR <15 mL/min/1.73m²)?",
    },
    {
      id: "ex-macrolide-jaundice",
      question:
        "Previous history of macrolide-associated jaundice or hepatic dysfunction?",
    },
    { id: "ex-severe-liver", question: "Severe liver disease?" },
    { id: "ex-typhoid", question: "Oral typhoid vaccine within ±3 days?" },
    { id: "int-simvastatin", question: "Taking simvastatin or lovastatin?" },
    { id: "int-ergot", question: "Taking ergotamine or dihydroergotamine?" },
    {
      id: "int-cisapride-pimozide",
      question:
        "Taking astemizole, cisapride, domperidone, pimozide, or terfenadine?",
    },
    { id: "int-ranolazine", question: "Taking ranolazine?" },
    { id: "int-ticagrelor", question: "Taking ticagrelor?" },
    { id: "int-colchicine", question: "Taking colchicine?" },
    { id: "int-midazolam-oral", question: "Taking oral midazolam?" },
    {
      id: "int-cyp-inducers",
      question: "Taking strong CYP3A4 inducers?",
      hint:
        "efavirenz, etravirine, nevirapine, rifampicin, rifabutin, rifapentine, phenytoin, carbamazepine, phenobarbital, St John's wort",
      linkRefs: ["indiana-flockhart"],
    },
  ],

  cautions: [
    {
      id: "caution-breastfeeding",
      text:
        "Breastfeeding — monitor nursing infant for GI disturbances, oral candida, rashes, drowsiness, irritability, sweating, loss of appetite",
    },
    {
      id: "caution-warfarin",
      text:
        "Coumarin anticoagulants (e.g. warfarin) — INR may rise. Advise INR monitoring and to seek attention if bleeding.",
    },
    {
      id: "caution-doac",
      text:
        "DOACs (apixaban, dabigatran, edoxaban, rivaroxaban) — increased risk of bleeding. Advise to seek attention if bleeding.",
    },
    {
      id: "caution-statins",
      text:
        "Statins (simvastatin contraindicated — see exclusions) — counsel on rhabdomyolysis risk. Consider withholding statin.",
    },
    {
      id: "caution-ccb",
      text:
        "Calcium channel blockers (amlodipine, diltiazem, felodipine, lercanidipine, nifedipine, verapamil) — risk of hypotension. Advise avoid driving if dizzy.",
    },
    {
      id: "caution-hypoglycaemics",
      text:
        "Oral hypoglycaemics/insulin — risk of hypoglycaemia. Advise more frequent blood glucose monitoring.",
    },
    {
      id: "caution-digoxin",
      text:
        "Digoxin — may increase levels. Advise on symptoms of toxicity (blurred vision, diarrhoea, confusion, dizziness, nausea).",
    },
    {
      id: "caution-hypokalaemia",
      text:
        "Medicines causing hypokalaemia (diuretics, corticosteroids, xanthines) — electrolyte disturbance risk. Advise to contact prescriber.",
    },
    {
      id: "caution-excipients",
      text:
        "Check excipients: lactose, sucrose, fructose, sorbitol (hereditary intolerances); aspartame (phenylketonuria)",
    },
  ],

  counselling: [
    "Can be taken with or without food",
    "Tablets should be swallowed whole with water",
    "Complete the full 5-day course even if symptoms improve",
    "Manage initial pain and swelling with appropriate OTC pain relief and cold compress",
    "Draw a line around the border of erythema (or take clear photos) and monitor",
    "Return to pharmacy if symptoms worsen at any time OR do not improve after completing 5-day course",
    "Seek urgent help if signs of sepsis develop — call 999 or attend A&E",
    "Oral suspension may taste bitter — drinking juice or water soon after can help",
  ],

  counsellingPoints: [
    { text: "Can be taken with or without food" },
    { text: "Tablets should be swallowed whole with water" },
    { text: "Complete the full 5-day course even if symptoms improve" },
    {
      text:
        "Manage initial pain and swelling with appropriate OTC pain relief (paracetamol or ibuprofen) and cold compress",
    },
    {
      text:
        "Draw a line around the border of erythema (or take clear photos) and monitor",
    },
    {
      text:
        "Return to pharmacy if symptoms worsen at any time OR do not improve after completing 5-day course",
    },
    { text: "Seek urgent help if signs of sepsis develop — call 999 or attend A&E" },
    {
      text:
        "Oral suspension may taste bitter — drinking juice or water soon after can help",
    },
    {
      text:
        "Children should be encouraged to swallow solid oral dose forms where possible",
      linkRefs: [
        "medicines-for-children-clari",
        "medicines-for-children-giving-tablets",
      ],
    },
  ],

  outcomeLinkRefs: [
    "target-selfcare-leaflet",
    "nhs-insect-bites",
    "ukhsa-tick-awareness",
    "anaphylaxis-campaign",
  ],
};
